import React, { useContext, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { v4 } from 'uuid';
import { CustomButtonNovo } from '~/components/Buttons/CustomButtonNovo';
import { BuscaProduto } from '~/components/NovosInputs';
import { LojaContext } from '~/context/loja';
import { useProduto } from '~/pages/Produto/hooks/useProduto';
import { Associado } from '~/pages/Produto/types';
import { formatCurrencyAsText } from '~/utils/functions';

export const Form: React.FC = () => {
  const {
    register,
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
    setAssociadosPai,
    associadosPai,
    setAssociadosFilho,
    associadosFilho,
  } = useProduto();

  const { loja } = useContext(LojaContext);
  const divInput = useRef<HTMLDivElement | null>(null);

  function handleClearAssoc() {
    setValue('per_desc_assoc', '');
    setValue('per_acresc_assoc', '');
    setValue('cod_associado', { value: undefined, label: '' });
  }

  const messageToClearEquivalentes = async () => {
    const { isConfirmed } = await Swal.fire({
      title: `Deseja realmente alterar o papel?`,
      text: 'Alternar o papel do Associado (Pai ou Filho) irá limpar as dependências já existente',
      showDenyButton: true,
      confirmButtonText: 'SIM',
      denyButtonText: 'Não',
    });

    return isConfirmed;
  };

  async function handleAddAssoc() {
    const { tipo_associado, assoc_id_edit, associado } = getValues();

    if (watch('cod_produto') === associado?.cod_produto) {
      return toast.warning('Não é possivel associar um produto a ele mesmo');
    }

    const assoc =
      watch('tipo_associado') === 1 ? associadosPai : associadosFilho;

    const alreadyExists = assoc.some((item: any) => {
      return Number(item.cod_produto) === Number(associado.cod_produto);
    });

    if (alreadyExists && !assoc_id_edit) {
      return toast.warning('Este produto já foi adicionado na tabela.');
    }

    const hasExists = assoc.find((item: any) => {
      return item.id === assoc_id_edit;
    });

    if (hasExists) {
      const newAssociados = assoc.map((item: any) => {
        if (item.id === assoc_id_edit) {
          item.cod_produto = associado.cod_produto;
          item.des_produto = associado.des_produto;
          item.des_departamento = associado.des_departamento;
          item.per_desc_assoc = formatCurrencyAsText(
            associado.per_desc_assoc ?? 0,
          );
          item.qtd_estoque = associado.qtd_est_atual;
          item.per_acresc_assoc = formatCurrencyAsText(
            associado.per_acresc_assoc ?? 0,
          );
          item.flg_atual_vda_assoc = associado.flg_atual_vda_assoc ?? false;
        }
        return item;
      });

      setValue('associados', newAssociados);

      if (tipo_associado === 1) {
        setAssociadosPai(newAssociados);
        setValue('associados_pai', newAssociados);
      }
      if (tipo_associado === 2) {
        setAssociadosFilho(newAssociados);
        setValue('associados_filho', newAssociados);
      }

      setValue('assoc_id_edit', '');

      return handleClearAssoc();
    }

    if (watch('tipo_associado') === 1) {
      if (
        associadosPai.length > 0 &&
        associadosPai[0].cod_produto !== associado.cod_produto
      ) {
        return toast.warning(`O Produto pode conter apenas um pai.`);
      }
    }

    const regs: Associado = {
      id: v4(),
      cod_produto: associado.cod_produto,
      des_produto: associado.des_produto,
      des_departamento: associado.des_departamento,
      per_desc_assoc: formatCurrencyAsText(associado.per_desc_assoc || 0),
      qtd_estoque: associado.qtd_est_atual,
      per_acresc_assoc: formatCurrencyAsText(associado.per_acresc_assoc ?? 0),
      flg_atual_vda_assoc: associado.flg_atual_vda_assoc ?? false,
    };

    if (tipo_associado === 1) {
      if (associadosFilho.length > 0) {
        const shouldClear = await messageToClearEquivalentes();
        if (shouldClear) {
          setAssociadosFilho([]);
          setValue('associados_filho', []);
        } else {
          return;
        }
      }

      setAssociadosPai([...associadosPai, regs]);

      setValue('associados_pai', [...associadosPai, regs]);
    }
    if (tipo_associado === 2) {
      if (associadosPai.length > 0) {
        const shouldClear = await messageToClearEquivalentes();
        if (shouldClear) {
          setAssociadosPai([]);
          setValue('associados_pai', []);
        } else {
          return;
        }
      }

      setAssociadosFilho([...associadosFilho, regs]);
      setValue('associados_filho', [...associadosFilho, regs]);
    }

    handleClearAssoc();
  }

  return (
    <>
      <Row>
        <Col sm={12} md={8} lg={6} xl={6} ref={divInput}>
          <BuscaProduto
            label={
              watch('tipo_associado') === 1 ? 'Produto Pai' : 'Produto Filho'
            }
            name="cod_associado"
            placeholder="Informe pelo menos 3 caracteres do Código, Descrição ou GTIN"
            customOptions={{
              hideBuscarPor: true,
              showSelecionarItensContendo: false,
              buscarPor: ['Associados'],
              buscaDerivados: false,
              buscaPadrao: 'Associados',
            }}
            register={register}
            control={control}
            disabled={false}
            setValue={setValue}
            buscaNasLojas={loja.cod_loja}
            isError={!!errors.cod_associado}
            getProduct={async (selected: any) => {
              const produto = selected[0];
              setValue('associado', produto);
              setValue('cod_associado', {
                label: `${produto.cod_produto} - ${produto.des_produto}`,
                value: produto.cod_produto,
              });

              const naoAssocia =
                watch('tipo_associado') !== 1 && produto.qtd_est_atual > 0;

              if (naoAssocia) {
                await Swal.fire({
                  icon: 'warning',
                  title: 'PLU contém estoque',
                  text: 'Não é possível associá-lo ao PLU. Regularize a situação para prosseguir.',
                  showDenyButton: false,
                  confirmButtonText: 'OK',
                });
              }
            }}
          />
        </Col>
        {/* -------------------campos ocultados task 2776-------------------}
        {/* <Col sm={12} md={4} lg={3} xl={2}>
          <ToggleDefault
            labelText="Assumir Valor de Venda do Produto Pai?"
            setChecked={formAssociado.flg_atual_vda_assoc.value}
            onSwitch={() =>
              changeFormAssociado({
                ...formAssociado,
                flg_atual_vda_assoc: {
                  ...formAssociado.flg_atual_vda_assoc,
                  value: !formAssociado.flg_atual_vda_assoc.value,
                },
              })
            }
          />
        </Col>

        <Col sm={12} md={4} lg={3} xl={2}>
          <InputPercent
            label="% Val. Venda"
            min={0}
            max={100}
            maxLength={5}
            decimals={2}
            placeholder="0,00"
            isDisabled={watch('tipo_associado') !== 1}
            name="per_desc_assoc"
            register={register}
            control={control}
            isError={!!errors.per_desc_assoc}
            showIcon
            autoComplete="off"
            onInput={(ev: any) => {
              const serializedValue = moedaFormatada(ev.target.value);

              changeFormAssociado({
                ...formAssociado,
                per_desc_assoc: {
                  ...formAssociado.per_desc_assoc,
                  value: serializedValue.includes('-')
                    ? serializedValue.replace('-', '')
                    : serializedValue,
                },
              });
            }}
          />
        </Col>

        <Col sm={12} md={4} lg={3} xl={2}>
          <InputPercent
            label="% Val. Despesa"
            min={0}
            max={100}
            maxLength={5}
            decimals={2}
            placeholder="0,00"
            name="per_acresc_assoc"
            register={register}
            control={control}
            isError={!!errors.per_acresc_assoc}
            showIcon
            autoComplete="off"
            onInput={(ev: any) => {
              const serializedValue = moedaFormatada(ev.target.value);

              changeFormAssociado({
                ...formAssociado,
                per_acresc_assoc: {
                  ...formAssociado.per_acresc_assoc,
                  value: serializedValue.includes('-')
                    ? serializedValue.replace('-', '')
                    : serializedValue,
                },
              });
            }}
          />
        </Col> */}

        <Col
          sm={12}
          md={4}
          lg={6}
          xl={6}
          className="d-flex gap-2 align-items-center align-self-end justify-content-end"
        >
          <CustomButtonNovo
            variant="confirm"
            onClick={async () => {
              await handleAddAssoc();
            }}
            label="Adicionar"
          />
          <CustomButtonNovo
            variant="clear"
            onClick={handleClearAssoc}
            label="Limpar"
          />
        </Col>
      </Row>
    </>
  );
};
